interface NativeSlider {
	wrapper: HTMLElement | null
	index: number
	slideWidth: number
	slides: HTMLElement[]
	slider: HTMLElement | null
	slidesPerView: number
	progress: HTMLProgressElement | null
	loop: boolean
	autoplay: boolean
	autoplayInterval: number
	autoplayTimer: number | null
	init(wrapper: HTMLElement, progress: HTMLProgressElement | null, loop: boolean, autoplay?: boolean, autoplayInterval?: number): void
	getSlideWidth(): number
	getMaxIndex(): number
	updateIndexFromScroll(): void
	updateProgress(): void
	scrollPrev(): void
	scrollNext(): void
	scrollTo(index: number): void
	startAutoplay(): void
	stopAutoplay(): void
}

export default {
	name: 'nativeSlider',
	component(): NativeSlider {
		return {
			wrapper: null,
			index: 0,
			slideWidth: 0,
			slides: [],
			slider: null,
			slidesPerView: 1,
			progress: null,
			loop: false,
			autoplay: false,
			autoplayInterval: 3000,
			autoplayTimer: null,

			init(
				wrapper: HTMLElement,
				progress: HTMLProgressElement | null,
				loop: boolean = false,
				autoplay: boolean = false,
				autoplayInterval: number = 3000
			) {
				if (!wrapper) return

				this.wrapper = wrapper
				// Trouver l'élément scrollable (le slider)
				this.slider = this.wrapper.querySelector('.oa-slider') as HTMLElement
				if (!this.slider) return

				this.slides = Array.from(this.slider.querySelectorAll('.oa-slider__slide')) as HTMLElement[]
				this.progress = progress
				this.loop = loop
				this.autoplay = autoplay
				this.autoplayInterval = autoplayInterval

				// Initialiser le progress si présent
				if (this.progress) {
					this.updateProgress()
				}

				// Ajouter un listener sur le scroll pour mettre à jour automatiquement
				this.slider.addEventListener('scroll', () => {
					this.updateIndexFromScroll()
					if (this.progress) {
						this.updateProgress()
					}
				})

				// Gérer l'autoplay : pause au survol et reprise quand on quitte
				if (this.autoplay) {
					this.startAutoplay()

					this.wrapper.addEventListener('mouseenter', () => {
						this.stopAutoplay()
					})

					this.wrapper.addEventListener('mouseleave', () => {
						if (this.autoplay) {
							this.startAutoplay()
						}
					})

					// Pause lors d'une interaction utilisateur (scroll, touch)
					this.slider.addEventListener('touchstart', () => {
						this.stopAutoplay()
					})

					this.slider.addEventListener('mousedown', () => {
						this.stopAutoplay()
					})

					// Reprendre après une interaction si l'autoplay est toujours actif
					this.slider.addEventListener('touchend', () => {
						if (this.autoplay) {
							setTimeout(() => this.startAutoplay(), this.autoplayInterval)
						}
					})

					this.slider.addEventListener('mouseup', () => {
						if (this.autoplay) {
							setTimeout(() => this.startAutoplay(), this.autoplayInterval)
						}
					})
				}
			},

			getSlideWidth() {
				return this.slides[0]?.offsetWidth || 0
			},

			getMaxIndex() {
				return Math.max(this.slides.length - this.slidesPerView, 0)
			},

			updateIndexFromScroll() {
				const slideWidth = this.getSlideWidth()
				if (slideWidth === 0 || !this.slider) return

				// Calculer l'index basé sur la position de scroll
				const scrollLeft = this.slider.scrollLeft
				const newIndex = Math.round(scrollLeft / slideWidth)

				// S'assurer que l'index reste dans les limites
				const maxIndex = this.getMaxIndex()
				this.index = Math.min(Math.max(newIndex, 0), maxIndex)
			},

			updateProgress() {
				if (!this.progress || !this.slider) return

				const scrollWidth = this.slider.scrollWidth
				const clientWidth = this.slider.clientWidth
				const scrollLeft = this.slider.scrollLeft

				// Si le contenu ne dépasse pas la largeur visible, progress = 100%
				if (scrollWidth <= clientWidth) {
					this.progress.value = 100
					return
				}

				// Calculer la progression basée sur la position de scroll réelle
				// scrollLeft = 0 → 0%
				// scrollLeft = scrollWidth - clientWidth → 100%
				const maxScroll = scrollWidth - clientWidth
				const progressValue = (scrollLeft / maxScroll) * 100

				this.progress.value = Math.min(Math.max(progressValue, 0), 100)
			},

			scrollPrev() {
				if (this.loop) {
					// En mode loop, si on est à la première slide, aller à la dernière
					if (this.index <= 0) {
						const maxIndex = this.getMaxIndex()
						this.scrollTo(maxIndex)
					} else {
						const newIndex = Math.max(this.index - this.slidesPerView, 0)
						this.scrollTo(newIndex)
					}
				} else {
					// Comportement normal sans loop
					const newIndex = Math.max(this.index - this.slidesPerView, 0)
					this.scrollTo(newIndex)
				}
			},

			scrollNext() {
				if (this.loop) {
					const maxIndex = this.getMaxIndex()
					// En mode loop, si on est à la dernière slide, aller à la première
					if (this.index >= maxIndex) {
						this.scrollTo(0)
					} else {
						const newIndex = Math.min(this.index + this.slidesPerView, maxIndex)
						this.scrollTo(newIndex)
					}
				} else {
					// Comportement normal sans loop
					const maxIndex = this.getMaxIndex()
					const newIndex = Math.min(this.index + this.slidesPerView, maxIndex)
					this.scrollTo(newIndex)
				}
			},

			scrollTo(index: number) {
				if (!this.slider) return

				this.index = index
				this.slider.scrollTo({
					left: index * this.getSlideWidth(),
					behavior: 'smooth'
				})

				// Mettre à jour la progression après le scroll
				setTimeout(() => {
					this.updateProgress()
				}, 100)
			},

			startAutoplay() {
				this.stopAutoplay() // S'assurer qu'il n'y a pas de timer en cours

				if (!this.autoplay || this.slides.length === 0) return

				this.autoplayTimer = window.setInterval(() => {
					const maxIndex = this.getMaxIndex()

					if (this.loop) {
						// En mode loop, toujours passer à la slide suivante
						if (this.index >= maxIndex) {
							this.scrollTo(0)
						} else {
							this.scrollNext()
						}
					} else {
						// Sans loop, arrêter à la dernière slide
						if (this.index < maxIndex) {
							this.scrollNext()
						} else {
							this.stopAutoplay()
						}
					}
				}, this.autoplayInterval)
			},

			stopAutoplay() {
				if (this.autoplayTimer !== null) {
					clearInterval(this.autoplayTimer)
					this.autoplayTimer = null
				}
			}
		}
	}
}
